import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import StarIcon from '@mui/icons-material/Star';
import { useTheme, alpha } from '@mui/material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import Typography from '@mui/material/Typography';
import React from 'react';
import { Link } from 'react-router-dom';

import { useLicenseEntitlement } from '../../hooks/useLicenseEntitlement';
import { SubscriptionTier } from '../../models/Subscription';

/**
 * Plan tier definition with pricing and features
 */
interface PlanTier {
  tier: SubscriptionTier;
  name: string;
  price: number;
  billingPeriod: string;
  description: string;
  highlighted?: boolean;
  badge?: string;
}

const PLAN_TIERS: PlanTier[] = [
  {
    tier: SubscriptionTier.Free,
    name: 'Free',
    price: 0,
    billingPeriod: 'forever',
    description: 'Try HealioSpace with basic features',
  },
  {
    tier: SubscriptionTier.Basic,
    name: 'Basic',
    price: 19,
    billingPeriod: '/month',
    description: 'Perfect for solo practitioners',
  },
  {
    tier: SubscriptionTier.Plus,
    name: 'Plus',
    price: 49,
    billingPeriod: '/month',
    description: 'For growing practices',
    highlighted: true,
    badge: 'POPULAR',
  },
  {
    tier: SubscriptionTier.Premium,
    name: 'Premium',
    price: 99,
    billingPeriod: '/month',
    description: 'Advanced features for established practices',
  },
  {
    tier: SubscriptionTier.Enterprise,
    name: 'Enterprise',
    price: 199,
    billingPeriod: '/month',
    description: 'Complete solution with white labeling',
  },
];

/**
 * Key features to highlight in comparison
 */
const KEY_FEATURES: { id: string; name: string; tiers: SubscriptionTier[] }[] = [
  {
    id: 'clients',
    name: 'Client Management',
    tiers: [
      SubscriptionTier.Free,
      SubscriptionTier.Basic,
      SubscriptionTier.Plus,
      SubscriptionTier.Premium,
      SubscriptionTier.Enterprise,
    ],
  },
  {
    id: 'telehealth',
    name: 'Telehealth Sessions',
    tiers: [
      SubscriptionTier.Basic,
      SubscriptionTier.Plus,
      SubscriptionTier.Premium,
      SubscriptionTier.Enterprise,
    ],
  },
  {
    id: 'messaging',
    name: 'Secure Messaging',
    tiers: [
      SubscriptionTier.Basic,
      SubscriptionTier.Plus,
      SubscriptionTier.Premium,
      SubscriptionTier.Enterprise,
    ],
  },
  {
    id: 'groupPrograms',
    name: 'Group Programs',
    tiers: [SubscriptionTier.Plus, SubscriptionTier.Premium, SubscriptionTier.Enterprise],
  },
  {
    id: 'advancedAnalytics',
    name: 'Advanced Analytics',
    tiers: [SubscriptionTier.Plus, SubscriptionTier.Premium, SubscriptionTier.Enterprise],
  },
  {
    id: 'teamManagement',
    name: 'Team Management',
    tiers: [SubscriptionTier.Plus, SubscriptionTier.Premium, SubscriptionTier.Enterprise],
  },
  {
    id: 'customIntegrations',
    name: 'Custom Integrations',
    tiers: [SubscriptionTier.Premium, SubscriptionTier.Enterprise],
  },
  { id: 'whiteLabeling', name: 'White Labeling', tiers: [SubscriptionTier.Enterprise] },
  { id: 'auditLogs', name: 'HIPAA Audit Logs', tiers: [SubscriptionTier.Enterprise] },
];

export interface PlanComparisonProps {
  highlightTier?: SubscriptionTier;
  showCurrentPlan?: boolean;
  compact?: boolean;
  maxTiers?: number;
  onSelectPlan?: (tier: SubscriptionTier) => void;
}

/**
 * PlanComparison Component (HA-109)
 * Side-by-side tier comparison showing features per plan
 */
const PlanComparison: React.FC<PlanComparisonProps> = ({
  highlightTier,
  showCurrentPlan = true,
  compact = false,
  maxTiers,
  onSelectPlan,
}) => {
  const theme = useTheme();
  const { tier: currentTier } = useLicenseEntitlement();

  // Filter and limit tiers if needed
  const displayTiers = maxTiers ? PLAN_TIERS.slice(0, maxTiers) : PLAN_TIERS;

  const isCurrentPlan = (planTier: SubscriptionTier) => planTier === currentTier;
  const isHighlighted = (plan: PlanTier) => plan.highlighted || plan.tier === highlightTier;

  const handleSelectPlan = (tier: SubscriptionTier) => {
    if (onSelectPlan) {
      onSelectPlan(tier);
    }
  };

  if (compact) {
    // Compact horizontal card layout
    return (
      <Box
        sx={{
          display: 'flex',
          gap: 2,
          overflowX: 'auto',
          pb: 1,
        }}
      >
        {displayTiers.map((plan) => (
          <Card
            key={plan.tier}
            elevation={isHighlighted(plan) ? 4 : 1}
            sx={{
              minWidth: 200,
              flex: '0 0 auto',
              position: 'relative',
              border: isCurrentPlan(plan.tier)
                ? `2px solid ${theme.palette.success.main}`
                : isHighlighted(plan)
                  ? `2px solid ${theme.palette.primary.main}`
                  : `1px solid ${theme.palette.divider}`,
            }}
          >
            {plan.badge && (
              <Chip
                label={plan.badge}
                size="small"
                color="primary"
                sx={{
                  position: 'absolute',
                  top: -10,
                  right: 10,
                  fontWeight: 600,
                  fontSize: '0.7rem',
                }}
              />
            )}
            <CardContent sx={{ p: 2 }}>
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 600,
                }}
              >
                {plan.name}
              </Typography>
              <Typography
                variant="h5"
                color="primary"
                sx={{
                  fontWeight: 700,
                }}
              >
                ${plan.price}
                <Typography
                  component="span"
                  variant="caption"
                  sx={{
                    color: 'text.secondary',
                  }}
                >
                  {plan.billingPeriod}
                </Typography>
              </Typography>
              {isCurrentPlan(plan.tier) && showCurrentPlan && (
                <Chip label="Current" size="small" color="success" sx={{ mt: 1 }} />
              )}
            </CardContent>
          </Card>
        ))}
      </Box>
    );
  }

  // Full comparison table
  return (
    <Box sx={{ overflowX: 'auto' }}>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: `200px repeat(${displayTiers.length}, 1fr)`,
          gap: 0,
          minWidth: 600,
        }}
      >
        {/* Header row */}
        <Box sx={{ p: 2 }} />
        {displayTiers.map((plan) => (
          <Box
            key={plan.tier}
            sx={{
              p: 2,
              textAlign: 'center',
              backgroundColor: isHighlighted(plan)
                ? alpha(theme.palette.primary.main, 0.08)
                : 'transparent',
              borderBottom: `1px solid ${theme.palette.divider}`,
              position: 'relative',
            }}
          >
            {plan.badge && (
              <Chip
                label={plan.badge}
                size="small"
                color="primary"
                icon={<StarIcon fontSize="small" />}
                sx={{
                  position: 'absolute',
                  top: 8,
                  right: 8,
                  fontWeight: 600,
                  fontSize: '0.65rem',
                }}
              />
            )}
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
              }}
            >
              {plan.name}
            </Typography>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                color: 'primary.main',
              }}
            >
              ${plan.price}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary',
              }}
            >
              {plan.billingPeriod}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary',
                mt: 1,
              }}
            >
              {plan.description}
            </Typography>
            {isCurrentPlan(plan.tier) && showCurrentPlan ? (
              <Chip label="Current Plan" size="small" color="success" sx={{ mt: 2 }} />
            ) : (
              <Button
                component={Link}
                to="/subscription"
                variant={isHighlighted(plan) ? 'contained' : 'outlined'}
                size="small"
                sx={{ mt: 2 }}
                onClick={() => handleSelectPlan(plan.tier)}
              >
                {plan.tier > currentTier ? 'Upgrade' : 'Select'}
              </Button>
            )}
          </Box>
        ))}

        {/* Feature rows */}
        {KEY_FEATURES.map((feature, index) => (
          <React.Fragment key={feature.id}>
            <Box
              sx={{
                p: 2,
                display: 'flex',
                alignItems: 'center',
                backgroundColor:
                  index % 2 === 0 ? alpha(theme.palette.grey[100], 0.5) : 'transparent',
                borderBottom: `1px solid ${theme.palette.divider}`,
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 500,
                }}
              >
                {feature.name}
              </Typography>
            </Box>
            {displayTiers.map((plan) => (
              <Box
                key={`${feature.id}-${plan.tier}`}
                sx={{
                  p: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor:
                    index % 2 === 0
                      ? isHighlighted(plan)
                        ? alpha(theme.palette.primary.main, 0.08)
                        : alpha(theme.palette.grey[100], 0.5)
                      : isHighlighted(plan)
                        ? alpha(theme.palette.primary.main, 0.04)
                        : 'transparent',
                  borderBottom: `1px solid ${theme.palette.divider}`,
                }}
              >
                {feature.tiers.includes(plan.tier) ? (
                  <CheckIcon sx={{ color: theme.palette.success.main }} />
                ) : (
                  <CloseIcon sx={{ color: theme.palette.grey[400] }} />
                )}
              </Box>
            ))}
          </React.Fragment>
        ))}
      </Box>
    </Box>
  );
};

export default PlanComparison;
